import * as React from "react";
import { County, GetNearbyCounties } from "@app/geography";
import GeographyService from "@app/geography/geographyService";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

interface NearbyCountiesPanelContentProps {
  countySSAs: string[];
  onSubmitNearbyCounties: (updateCounties: County[], regionStatus: RegionSetStatusType) => void;
}

interface NearbyCountiesPanelContentState {
  addCountiesRadius: number;
  countyCopyRegionStatus: RegionSetStatusType;
  limitSearchToCurentStates: boolean;
  submitClicked: boolean;
}

class WrappedNearbyCountiesPanelContent extends React.Component<NearbyCountiesPanelContentProps & FlyoutContextOutgoingProps, NearbyCountiesPanelContentState> {
  private geographyService = new GeographyService();

  constructor(props: NearbyCountiesPanelContentProps & FlyoutContextOutgoingProps) {
    super(props);

    this.state = {
      addCountiesRadius: -1,
      countyCopyRegionStatus: RegionSetStatusType.Unknown,
      limitSearchToCurentStates: false,
      submitClicked: false,
    };
  }

  public render() {
    const disableSubmit = this.state.addCountiesRadius < 0 || this.state.countyCopyRegionStatus === RegionSetStatusType.Unknown;
    const submitButtonContents = this.state.submitClicked
      ? <><QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin align-text-top" description="Loading" />&nbsp;Please Wait</>
      : <>Submit</>;

    return (
      <>
        <p>Please select a search radius and a status for the added counties...</p>
        <div className="form-horizontal">
          <div className="form-group row">
            <div className="col-12">
              <QESSelect label="Radius" testId="nearby-counties-panel__radius" className="form-control" onChange={(e) => this.handleAddCountiesRadiusChange(e.target.value)}>
                <option value="-1" />
                <option value="0">Adjacent Counties</option>
                <option value="50">50 miles</option>
                <option value="100">100 miles</option>
                <option value="150">150 miles</option>
              </QESSelect>
            </div>
          </div>
          <div className="form-group row">
            <div className="col-12">
              <QESSelect label="County Status" testId="nearby-counties-panel__county-status" className="form-control" value={this.state.countyCopyRegionStatus} onChange={(e) => this.handleAddCountiesRegionStatusChange(e.target.value as RegionSetStatusType)}>
                <option value={RegionSetStatusType.Unknown} />
                <option value={RegionSetStatusType.Active}>{RegionSetStatusType.Active}</option>
                <option value={RegionSetStatusType.Approved}>{RegionSetStatusType.Approved}</option>
                <option value={RegionSetStatusType.Opportunity}>{RegionSetStatusType.Opportunity}</option>
                <option value={RegionSetStatusType.Pending}>{RegionSetStatusType.Pending}</option>
              </QESSelect>
            </div>
          </div>
          <div className="form-group row">
            <div className="col-12">
              <QESCheckbox
                label="Limit the search to current service area states only"
                checked={this.state.limitSearchToCurentStates}
                onChange={(e) => this.handleLimitSearchChange(e.target.checked)}
                testId="nearby-counties__current-service-area-only"
              />
            </div>
          </div>
          <FlyoutFooter
            submitButton={<QESButton qesTestId="nearby-counties-panel-content_submit" qesType="primary" disabled={disableSubmit || this.state.submitClicked} onClick={this.handleSubmitNearbyCounties}>
              {submitButtonContents}
            </QESButton>}
          />
        </div>
      </>
    );
  }

  private handleAddCountiesRadiusChange = (radius: string) => {
    const addCountiesRadius = Number(radius);
    this.setState({ addCountiesRadius });
  }

  private handleAddCountiesRegionStatusChange = (countyCopyRegionStatus: RegionSetStatusType) => {
    this.setState({ countyCopyRegionStatus });
  }

  private handleLimitSearchChange = (limitSearchToCurentStates: boolean) => {
    this.setState({ limitSearchToCurentStates });
  }

  private handleSubmitNearbyCounties = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    this.setState({ submitClicked: true });

    const updateCounties =
      await this.geographyService.getNearbyCounties(
        {
          CountySSAs: this.props.countySSAs,
          Distance: this.state.addCountiesRadius,
          IncludeSourceCounties: false,
          LimitToCurrentStates: this.state.limitSearchToCurentStates,
        } as GetNearbyCounties);

    this.props.onSubmitNearbyCounties(updateCounties, this.state.countyCopyRegionStatus);
    this.props.handleCloseFlyout();
  }
}

const NearbyCountiesPanelContent = withFlyoutContext(WrappedNearbyCountiesPanelContent);
export default NearbyCountiesPanelContent;
